import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Backend, PERMISSION} from '@generated-backend-types';
import {StrictFormGroup} from '@browsepedia/ng-forms';
import {NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {HttpErrorResponse} from '@angular/common/http';
import AuthorData = Backend.App.Data.Dashboard.Administration.AuthorData;
import Publication = Backend.App.Resources.Dashboard.Administration.Publication;
import AuthorTypeEnum = Backend.App.Enums.AuthorTypeEnum;
import {throwError} from 'rxjs';
import {
  MultiSelectEnumComponent
} from '../../../../../../components/form-inputs/multi-select-enum/multi-select-enum.component';
import {FormGroupComponent} from '../../../../../../components/form-inputs/form-group/form-group.component';
import {
  MultiSelectWithServiceComponent
} from '../../../../../../components/form-inputs/multi-select-with-service/multi-select-with-service.component';
import {AuthorService} from '../../../../../../services/api/administration/author.service';
import {AddableTeamMembersService} from '../../../../../../services/api/administration/addable-team-members.service';
import {FormErrorService} from '../../../../../../services/form-error.service';
import {UserSessionService} from '../../../../../../services/user-session.service';
import {NotEnoughPermissionsError} from '../../../../../../errors/NotEnoughPermissionsError';

@Component({
  selector: 'app-authenticated-administration-publications-authors-create',
  templateUrl: './create.component.html',
  imports: [
    MultiSelectEnumComponent,
    FormGroupComponent,
    ReactiveFormsModule,
    MultiSelectWithServiceComponent,
    TranslatePipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateComponent implements OnInit {
  @Input({required: true}) public publication!: Publication;

  @Output() public authorCreated = new EventEmitter<void>();

  public form: StrictFormGroup<AuthorData> = this.fb.group({
    type: this.fb.control(AuthorTypeEnum.EXTERNAL, [Validators.required]),
    name: this.fb.control(''),
    team_member_id: this.fb.control(''),
    publication_id: this.fb.control(''),
  });

  protected readonly AuthorTypeEnum = AuthorTypeEnum;

  public constructor(
    private readonly fb: NonNullableFormBuilder,
    private readonly authorService: AuthorService,
    public readonly addableTeamMembersService: AddableTeamMembersService,
    private readonly toastrService: ToastrService,
    private readonly translate: TranslateService,
    private readonly formErrorService: FormErrorService,
    private readonly userSessionService: UserSessionService,
  ) {
  }

  public ngOnInit() {
    this.addableTeamMembersService.forPublication(this.publication);

    this.form.controls.type.valueChanges.subscribe(() => {
      this.form.controls.name.setValue('');
      this.form.controls.team_member_id.setValue('');
    })
  }

  public save() {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    this.form.controls.publication_id.setValue(this.publication.id);
    const data = this.form.value as AuthorData;

    if (!this.userSessionService.hasAllPermission([PERMISSION.ADMINISTRATION_AUTHOR_CREATE_ALL])) {
      throwError(() => new NotEnoughPermissionsError());
    } else {
      this.authorService.create(data).subscribe({
        next: () => {
          this.toastrService.success(this.translate.instant('authenticated.administration.publications.edit.authors.create.success'));
          this.authorCreated.emit();
        },
        error: err => {
          if (err instanceof NotEnoughPermissionsError) {
            this.toastrService.error(this.translate.instant('misc.not_enough_permissions_error'));
          } else if (err instanceof HttpErrorResponse) {
            this.form.markAllAsTouched();
            this.form.updateValueAndValidity({onlySelf: false, emitEvent: true});
            this.formErrorService.mapErrorToForm(this.form, err);
          } else {
            this.toastrService.error(this.translate.instant('authenticated.administration.publications.edit.authors.create.error'));
          }
        }
      });
    }
  }
}
